import { useEffect, useMemo, useState } from 'react';
import { Alert, Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

interface RepositoryOption {
  name: string;
  fullName: string;
}

interface RepositoryResponse {
  repositories: RepositoryOption[];
}

export const GitHubAppRepoPickerField = (
  props: FieldExtensionComponentProps<string>,
) => {
  const { onChange, rawErrors, required, formData } = props;
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [repositories, setRepositories] = useState<RepositoryOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string>();

  useEffect(() => {
    let cancelled = false;

    const loadRepositories = async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('github-repo-picker');
        const response = await fetchApi.fetch(`${baseUrl}/repositories`);
        if (!response.ok) throw new Error(`Repository lookup returned ${response.status}`);
        const result = (await response.json()) as RepositoryResponse;
        if (!cancelled) setRepositories(result.repositories);
      } catch {
        if (!cancelled) {
          setLoadError('Could not load repository suggestions. You can still type the repository name.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadRepositories();
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi]);

  const selectedValue = useMemo(() => {
    if (!formData) return null;
    return repositories.find(repo => repo.name === formData) ?? formData;
  }, [formData, repositories]);

  return (
    <Box>
      <Autocomplete<RepositoryOption, false, false, true>
        freeSolo
        autoSelect
        options={repositories}
        value={selectedValue}
        loading={loading}
        getOptionLabel={option => typeof option === 'string' ? option : option.fullName}
        isOptionEqualToValue={(option, value) =>
          typeof value !== 'string' && option.fullName === value.fullName
        }
        onChange={(_, value) => {
          if (typeof value === 'string') onChange(value.trim() || undefined);
          else onChange(value?.name);
        }}
        onInputChange={(_, value, reason) => {
          if (reason === 'input') onChange(value.trim() || undefined);
        }}
        renderInput={params => (
          <TextField
            {...params}
            label="Existing CTM-Engineering repository"
            required={required}
            error={Boolean(rawErrors?.length)}
            helperText={
              rawErrors?.length
                ? rawErrors.join(', ')
                : loadError ?? (loading ? 'Loading repository suggestions…' : 'Select a suggestion or enter a repository name')
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        noOptionsText={loading ? 'Loading repositories…' : 'No suggestion found — enter the repository name'}
      />
      {loadError && <Alert severity="info" sx={{ mt: 1 }}>{loadError}</Alert>}
    </Box>
  );
};
